import { Primitive } from '@radix-ui/react-primitive';
import { type ComponentPropsWithoutRef, memo } from 'react';

import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const badge = tailwindVariants({
  base: 'inline-flex items-center justify-between gap-1 font-bold uppercase tracking-wide transition-colors',
  defaultVariants: {
    appearance: 'square',
    color: 'gray',
    size: 'small',
  },
  variants: {
    appearance: {
      pill: 'rounded-full',
      square: 'rounded-2',
    },
    color: {
      blue: 'ads:bg-primary bg-blue-8 ads:text-inverse text-blue-1',
      cyan: 'bg-cyan-11 text-gray-1',
      gray: 'bg-gray-4 text-gray-10',
      green: 'ads:bg-success bg-green-4 ads:text-success text-green-11',
      /** @deprecated Use 'gray' instead. British spelling kept for backwards compatibility (DES-25). */
      grey: 'bg-gray-4 text-gray-10',
      'light-blue': 'ads:bg-primary-subtle bg-blue-2 ads:text-accent text-blue-9',
      red: 'ads:bg-error bg-red-10 ads:text-inverse text-red-1',
      teal: 'ads:bg-promotional bg-teal-2 ads:text-promotional text-teal-10',
      yellow: 'ads:bg-warning bg-yellow-3 ads:text-warning text-orange-11',
    },
    size: {
      medium: 'h-5 px-1.5 text-12',
      small: 'h-4 px-1.5 text-10',
    },
  },
});

type BadgeVariants = VariantProps<typeof badge>;

export type BadgeProps = Omit<ComponentPropsWithoutRef<'div'>, keyof BadgeVariants> & BadgeVariants;

export const Badge = memo(({ appearance, className, color = 'gray', size = 'small', ...restOfProps }: BadgeProps) => {
  return <Primitive.div className={badge({ appearance, className, color, size })} {...restOfProps} />;
});

Badge.displayName = 'Badge';
